/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Shared data hooks + helpers for the GPU Pods screens and the launch wizard:
// GPU pricing/availability, customer sell-price folding, template catalog.

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pricingService, currencyService, templateService } from '../../../services/api';
import { BillingPeriod, CatalogCategory, GPUAvailability, GPUPricing, LaunchTemplate } from '../../../types';

/** Customer sell price, mirroring the backend ApplySellMargin:
 *  sales price = base × (1+markup) × (1+gateway);  total = sales price × (1+gst). */
export const useSellPrice = () => {
  const { data: currencyData } = useQuery({
    queryKey: ['currency-rates'],
    queryFn: () => currencyService.get().then((res) => res.data),
    staleTime: 5 * 60_000,
  });
  const gstRate: number = currencyData?.gst_rate ?? 18;
  const markupPct: number = currencyData?.markup_percent ?? 0;
  const gatewayPct: number = currencyData?.gateway_charge_percent ?? 0;
  const foldSellPrice = useCallback(
    (base: number): number => base * (1 + markupPct / 100) * (1 + gatewayPct / 100) * (1 + gstRate / 100),
    [markupPct, gatewayPct, gstRate],
  );
  return { gstRate, markupPct, gatewayPct, foldSellPrice };
};

/** GPU pricing + live availability. */
export const useGpuCatalog = () => {
  const { data: gpuPricingData, isLoading } = useQuery({
    queryKey: ['gpu-pricing'],
    queryFn: () => pricingService.getGPUPricing().then((res) => res.data),
  });
  const { data: gpuAvailabilityData } = useQuery({
    queryKey: ['gpu-availability'],
    queryFn: () => pricingService.getGPUAvailability().then((res) => res.data),
  });

  const gpus: GPUPricing[] = useMemo(
    () => (Array.isArray(gpuPricingData) ? gpuPricingData : gpuPricingData?.data || []),
    [gpuPricingData],
  );
  const availabilityList: GPUAvailability[] = useMemo(
    () => (Array.isArray(gpuAvailabilityData) ? gpuAvailabilityData : gpuAvailabilityData?.data || []),
    [gpuAvailabilityData],
  );
  const getAvailability = useCallback(
    (gpuType: string): GPUAvailability | undefined => availabilityList.find((a) => a.gpu_type === gpuType),
    [availabilityList],
  );
  return { gpus, isLoading, getAvailability };
};

/** Free GPUs rounded to 2dp (fractional GPUs are possible). */
export const freeGpus = (a?: GPUAvailability): number =>
  Math.round(((a?.available_gpus ?? 0) + Number.EPSILON) * 100) / 100;

export const isInStock = (a?: GPUAvailability): boolean => a !== undefined && freeGpus(a) > 0;

export const formatRupees = (n: number, digits = 0): string =>
  `₹${n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

/** ₹/hr when the value is large, 2dp when small. */
export const formatHourly = (n: number): string => `${formatRupees(n, n >= 100 ? 0 : 2)}/hr`;

export const PERIOD_MINUTES: Record<BillingPeriod, number> = {
  minute: 1,
  hour: 60,
  day: 1440,
  week: 10080,
  month: 43800,
  quarter: 131400,
  annual: 525600,
};

export const periodShort = (p: BillingPeriod): string =>
  p === 'minute' ? 'min' : p === 'hour' ? 'hr' : p === 'day' ? 'day' : p === 'week' ? 'wk' : p === 'month' ? 'mo' : p === 'quarter' ? 'qtr' : 'yr';

export const BILLING_PERIODS: { value: BillingPeriod; label: string }[] = [
  { value: 'minute', label: 'Per minute' },
  { value: 'hour', label: 'Per hour' },
  { value: 'day', label: 'Per day' },
  { value: 'month', label: 'Per month' },
  { value: 'quarter', label: 'Per quarter' },
  { value: 'annual', label: 'Per year' },
];

/** Short image name: "pytorch/pytorch:2.0" → "pytorch". */
export const shortImage = (image?: string): string =>
  (image || '').split('/').pop()?.split(':')[0] || image || '';

// ─── Template catalog ────────────────────────────────────────────────────────

export type TemplateTab = 'all' | 'popular' | 'fine-tuning' | 'notebooks' | 'inference' | 'comfyui';
export type ContentCategory = Exclude<TemplateTab, 'all' | 'popular'> | 'other';

export const TEMPLATE_TABS: { key: TemplateTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'popular', label: 'Popular' },
  { key: 'fine-tuning', label: 'Fine-tuning' },
  { key: 'notebooks', label: 'Notebooks' },
  { key: 'inference', label: 'Inference' },
  { key: 'comfyui', label: 'ComfyUI' },
];

export const CATEGORY_LABEL: Record<ContentCategory, string> = {
  'fine-tuning': 'Fine-tuning',
  notebooks: 'Notebook',
  inference: 'Serving',
  comfyui: 'Image gen',
  other: 'Base',
};

export function getTemplateCategory(template: LaunchTemplate): ContentCategory {
  const tags = (template.tags || []).map((tag) => tag.toLowerCase());
  const catName = (template.category?.name || '').toLowerCase();
  const text = `${template.name} ${template.description || ''}`.toLowerCase();

  const hasTag = (key: string) => tags.includes(key);
  const catIncludes = (key: string) => catName.includes(key);
  const includes = (key: string) => text.includes(key) || catIncludes(key);

  if (
    hasTag('comfyui') || hasTag('image') || catIncludes('comfy') || catIncludes('image') || catIncludes('diffusion') ||
    includes('stable diffusion') || includes('comfyui') || includes('automatic1111') || includes('fooocus') ||
    includes('invokeai')
  ) {
    return 'comfyui';
  }
  if (
    hasTag('notebook') || catIncludes('notebook') || catIncludes('jupyter') || includes('jupyter') ||
    includes('vscode') || includes('keras') || includes('rapids')
  ) {
    return 'notebooks';
  }
  if (
    hasTag('inference') || catIncludes('inference') || catIncludes('serving') || catIncludes('server') ||
    includes('vllm') || includes('ollama') || includes('triton') || includes('text generation')
  ) {
    return 'inference';
  }
  if (
    hasTag('fine-tuning') || catIncludes('fine-tuning') || catIncludes('fine tuning') || catIncludes('finetuning') ||
    includes('llama factory') || includes('unsloth') || includes('axolotl') || includes('trl') ||
    includes('torchtune') || includes('diffusers trainer')
  ) {
    return 'fine-tuning';
  }
  return 'other';
}

export function isPopularTemplate(template: LaunchTemplate): boolean {
  const tags = (template.tags || []).map((tag) => tag.toLowerCase());
  const catName = (template.category?.name || '').toLowerCase();
  return tags.includes('popular') || tags.includes('featured') || catName.includes('popular');
}

/** Global template catalog, flattened + de-duplicated. */
export const useTemplateCatalog = (enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ['template-catalog'],
    queryFn: () => templateService.getCatalog().then((res) => res.data),
    enabled,
  });
  const templates: LaunchTemplate[] = useMemo(() => {
    const cats: CatalogCategory[] = Array.isArray(data) ? data : data?.data || [];
    const seen = new Set<string>();
    const list: LaunchTemplate[] = [];
    cats.forEach((cat) =>
      (cat.templates || []).forEach((t) => {
        if (!seen.has(t.id)) {
          seen.add(t.id);
          list.push(t);
        }
      }),
    );
    return list;
  }, [data]);
  return { templates, isLoading };
};

/** First non-SSH port of a template, for the ":8888" hint. */
export const templatePortHint = (t: LaunchTemplate): string => {
  const ports = (t.ports || []).filter((p) => p !== 22);
  return ports.length ? ports.map((p) => `:${p}`).join(' ') : (t.ports || []).includes(22) ? ':22' : '';
};

/** The user's saved (custom) templates for a project - backs the Templates
 *  tab, its count badge and the Pods "Quick start" cards. */
export const useSavedTemplates = (projectId?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['my-templates', projectId || ''],
    queryFn: () =>
      projectId
        ? templateService.getProjectTemplates(projectId).then((res: any) => res.data)
        : templateService.getMyTemplates().then((res: any) => res.data),
  });
  const templates: LaunchTemplate[] = useMemo(() => {
    const list: LaunchTemplate[] = Array.isArray(data) ? data : data?.data || data?.templates || [];
    return list.filter((t) => t.scope === 'custom');
  }, [data]);
  return { templates, isLoading };
};
